import React from "react";
import { IconButton, Button, Stack, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import {
    useDateNavigation,
    UseDateNavigationProps,
} from "../models/use-date-navigation";

interface DateNavigationLayoutProps extends UseDateNavigationProps {
    disabled?: boolean;
}

/**
 * A layout component for date navigation controls.
 *
 * Renders previous day, today, and next day buttons, allowing users to navigate between dates.
 * The component uses the `useDateNavigation` hook to handle navigation logic and button states.
 *
 * @param selectedDate - The currently selected date.
 * @param dates - An array of available dates for navigation.
 * @param timezone - The timezone used for date calculations.
 * @param onDateChange - Callback invoked when the date changes.
 * @param disabled - Optional flag to disable all navigation controls.
 *
 * @returns A React element containing date navigation buttons.
 */
export const DateNavigationLayout: React.FC<DateNavigationLayoutProps> = ({
    selectedDate,
    dates,
    timezone,
    onDateChange,
    disabled = false,
}) => {
    const {
        handlePrevDay,
        handleNextDay,
        handleToday,
        isPrevDisabled,
        isNextDisabled,
        isTodayDisabled,
    } = useDateNavigation({ selectedDate, dates, timezone, onDateChange });

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Previous day">
                <span>
                    <IconButton
                        onClick={handlePrevDay}
                        disabled={disabled || !!isPrevDisabled}
                        size="small"
                        aria-label="Previous day"
                    >
                        <ChevronLeft />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Go to today">
                <span>
                    <Button
                        onClick={handleToday}
                        disabled={disabled || !!isTodayDisabled}
                        startIcon={<Today />}
                        variant="outlined"
                        size="small"
                    >
                        Today
                    </Button>
                </span>
            </Tooltip>

            <Tooltip title="Next day">
                <span>
                    <IconButton
                        onClick={handleNextDay}
                        disabled={disabled || !!isNextDisabled}
                        size="small"
                        aria-label="Next day"
                    >
                        <ChevronRight />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );
};
